import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import Table, { TableHeader } from 'components/Table'
import { Body } from 'components/Text'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import Button from 'components/Button'
import { selectCurrentMarketInfo, selectLiquidityBook, selectLiquidityBookQueryStatus, selectOrderbook, selectOrderbookQueryStatus, selectOrderbookWidth } from 'state/exchange/selectors'
import { formatNumber } from 'utils/prices'
import { increaseOrderbookWidth, setOrderPrice } from 'state/exchange/reducer'
import { ORDERBOOK_WIDTH_INCREASEMENT } from 'constants/defaults'
import { FetchStatus } from 'state/types'
import { fetchLiquidityBook, fetchOrderbook } from 'state/exchange/actions'
import { FlexDiv } from 'components/layout/flex'
import { selectCurrentMarketPrice } from 'state/prices/selectors'

type OrderbookTableProps = {
	mobile?: boolean
	display?: boolean
}

enum TableColumnAccessor {
	Amount = 'amount',
	Price = 'price',
	Time = 'time',
	Funding = 'fundingAccrued',
}

type OrderbookRow = {
	amount: number
	price: number
	buy: boolean
	liquidityAmount?: number
	isLiquidityOnly?: boolean
}

const Orderbook: FC<OrderbookTableProps> = ({ mobile, display }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const [loading, setLoading] = useState(true)
	const orderbook = useAppSelector(selectOrderbook)
	const liquidityBook = useAppSelector(selectLiquidityBook)
	const width = useAppSelector(selectOrderbookWidth)
	const orderbookQueryStatus = useAppSelector(selectOrderbookQueryStatus)
	const liquidityBookQueryStatus = useAppSelector(selectLiquidityBookQueryStatus)
	const marketInfo = useAppSelector(selectCurrentMarketInfo)
	const currentPrice = useAppSelector(selectCurrentMarketPrice)

	useEffect(() => {
		if (loading && (
				orderbookQueryStatus.status === FetchStatus.Error ||
				orderbookQueryStatus.status === FetchStatus.Success
			) && (
				liquidityBookQueryStatus.status === FetchStatus.Error ||
				liquidityBookQueryStatus.status === FetchStatus.Success
		)) {
			setLoading(false)
		}
	}, [orderbookQueryStatus, liquidityBookQueryStatus, loading, width])

	const { asks, bids } = useMemo(() => {
		if (!orderbook) return { asks: [], bids: [] }

		// Build orderbook data
		const rawAsks = orderbook.asks.map(e => ({ amount: e.amount, price: e.price, buy: false }))
		const rawBids = orderbook.bids.map(e => ({ amount: e.amount, price: e.price, buy: true }))

		// Build liquidity book map by price
		const liquidityMap = new Map<number, { buy: boolean; amount: number }>()
		if (liquidityBook) {
			liquidityBook.asks.forEach(e => {
				liquidityMap.set(e.price, { buy: false, amount: e.amount })
			})
			liquidityBook.bids.forEach(e => {
				liquidityMap.set(e.price, { buy: true, amount: e.amount })
			})
		}

		// Merge asks: orderbook asks + liquidity asks at same price
		const mergedAsks: OrderbookRow[] = []
		const askPrices = new Set([...rawAsks.map(a => a.price), ...Array.from(liquidityMap.entries()).filter(([_, v]) => !v.buy).map(([k]) => k)])
		askPrices.forEach(price => {
			const order = rawAsks.find(a => a.price === price)
			const liq = liquidityMap.get(price)
			if (liq && !liq.buy) {
				const amount = order?.amount ?? 0
				// Skip if both amount and liquidity are 0
				if (amount === 0 && liq.amount === 0) return
				mergedAsks.push({
					amount,
					price,
					buy: false,
					liquidityAmount: liq.amount,
					isLiquidityOnly: !order && liq.amount > 0,
				})
			} else if (order) {
				// Skip if amount is 0
				if (order.amount === 0) return
				mergedAsks.push({ amount: order.amount, price, buy: false })
			}
		})
		mergedAsks.sort((a, b) => b.price - a.price) // asks: high to low

		// Merge bids: orderbook bids + liquidity bids at same price
		const mergedBids: OrderbookRow[] = []
		const bidPrices = new Set([...rawBids.map(b => b.price), ...Array.from(liquidityMap.entries()).filter(([_, v]) => v.buy).map(([k]) => k)])
		bidPrices.forEach(price => {
			const order = rawBids.find(b => b.price === price)
			const liq = liquidityMap.get(price)
			if (liq && liq.buy) {
				const amount = order?.amount ?? 0
				// Skip if both amount and liquidity are 0
				if (amount === 0 && liq.amount === 0) return
				mergedBids.push({
					amount,
					price,
					buy: true,
					liquidityAmount: liq.amount,
					isLiquidityOnly: !order && liq.amount > 0,
				})
			} else if (order) {
				// Skip if amount is 0
				if (order.amount === 0) return
				mergedBids.push({ amount: order.amount, price, buy: true })
			}
		})
		mergedBids.sort((a, b) => b.price - a.price) // bids: high to low

		return { asks: mergedAsks, bids: mergedBids }
	}, [orderbook, liquidityBook])

	return (
		<OrderbookContainer $display={mobile || display} mobile={mobile}>
			{/* <StyledButton
				variant="flat"
				size="small"
				disabled={loading}
				onClick={async ()=>{
					setLoading(true)
					await dispatch(increaseOrderbookWidth(ORDERBOOK_WIDTH_INCREASEMENT))
					dispatch(fetchOrderbook())
					dispatch(fetchLiquidityBook())
				}}
			>
				{t('futures.market.trade.orders.load-more', {width: (width*100).toFixed()})}
			</StyledButton> */}
			<TableContainer>
				<OrderbookHeader>
					<HeaderColumn>
						<TableHeader>
							{t('futures.market.history.price-label')}
							(${marketInfo?.tokenY.symbol})
						</TableHeader>
					</HeaderColumn>
					<HeaderColumn>
						<TableHeader>
							{t('futures.market.history.amount-label')}
							(${marketInfo?.tokenX.symbol})
							<LiquidityAmount>(+liquidity)</LiquidityAmount>
						</TableHeader>
					</HeaderColumn>
				</OrderbookHeader>
				<SellTable
					data={asks}
					isLoading={loading}
					highlightRowsOnHover
					columnsDeps={[marketInfo]}
					columns={[
						{
							header: () =>
								<TableHeader>
									{t('futures.market.history.price-label')}
									(${marketInfo?.tokenY.symbol})
								</TableHeader>,
							accessorKey: TableColumnAccessor.Price,
							enableSorting: false,
							cell: (cellProps: any) => {
								const negative = !cellProps.row.original.buy

								return (
									<BookPriceValue
										negative={negative}
										onClick={() => dispatch(setOrderPrice(cellProps.row.original.price.toString()))}
										style={{ cursor: 'pointer' }}
									>
										{formatNumber(cellProps.row.original.price, { suggestDecimals: true })}
									</BookPriceValue>
								)
							},
							size: 100,
						},
						{
							header: () =>
								<TableHeader>
									{t('futures.market.history.amount-label')}
									(${marketInfo?.tokenX.symbol})
								</TableHeader>,
							accessorKey: TableColumnAccessor.Amount,
							enableSorting: false,
							cell: (cellProps: any) => {
								const isBuy = cellProps.row.original.buy
								const negative = !isBuy
								const amount = cellProps.getValue()
								const liquidityAmount = cellProps.row.original.liquidityAmount

								return (
									<BookVolumeValue negative={negative} >
										{formatNumber(Math.abs(amount), {
											suggestDecimals: true,
											truncateOver: 1e6,
										})}
										{liquidityAmount !== undefined && liquidityAmount > 0 && (
											<LiquidityAmount>
												(+{formatNumber(liquidityAmount, {
													suggestDecimals: true,
													truncateOver: 1e6,
												})})
											</LiquidityAmount>
										)}
									</BookVolumeValue>
								)
							},
							size: 100,
						},
					]}
				/>
			</TableContainer>
			<CurrentPriceSection>
				<CurrentPriceValue>
					{currentPrice ? formatNumber(currentPrice, { suggestDecimals: true }) + ' $' + marketInfo?.tokenY.symbol : '--'}
				</CurrentPriceValue>
			</CurrentPriceSection>
			<TableContainer>
				<BuyTable
					data={bids}
					isLoading={loading}
					highlightRowsOnHover
					columnsDeps={[marketInfo]}
					columns={[
						{
							header: () =>
								<TableHeader>
									{t('futures.market.history.price-label')}
									(${marketInfo?.tokenY.symbol})
								</TableHeader>,
							accessorKey: TableColumnAccessor.Price,
							enableSorting: false,
							cell: (cellProps: any) => {
								const negative = !cellProps.row.original.buy

								return (
									<BookPriceValue
										negative={negative}
										onClick={() => dispatch(setOrderPrice(cellProps.row.original.price.toString()))}
										style={{ cursor: 'pointer' }}
									>
										{formatNumber(cellProps.row.original.price, { suggestDecimals: true })}
									</BookPriceValue>
								)
							},
							size: 100,
						},
						{
							header: () =>
								<TableHeader>
									{t('futures.market.history.amount-label')}
									(${marketInfo?.tokenX.symbol})
								</TableHeader>,
							accessorKey: TableColumnAccessor.Amount,
							enableSorting: false,
							cell: (cellProps: any) => {
								const isBuy = cellProps.row.original.buy
								const negative = !isBuy
								const amount = cellProps.getValue()
								const liquidityAmount = cellProps.row.original.liquidityAmount

								return (
									<BookVolumeValue negative={negative} >
										{formatNumber(Math.abs(amount), {
											suggestDecimals: true,
											truncateOver: 1e6,
										})}
										{liquidityAmount !== undefined && liquidityAmount > 0 && (
											<LiquidityAmount>
												(+{formatNumber(liquidityAmount, {
													suggestDecimals: true,
													truncateOver: 1e6,
												})})
											</LiquidityAmount>
										)}
									</BookVolumeValue>
								)
							},
							size: 100,
						},
					]}
				/>
			</TableContainer>
		</OrderbookContainer>
	)
}

export default Orderbook

const StyledButton = styled(Button)`
	text-align: center;
	width: 100%
`

const OrderbookContainer = styled.div<{ mobile?: boolean; $display?: boolean }>`
	box-sizing: border-box;
	border-left: ${(props) => props.theme.colors.selectedTheme.border};
	height: 100%;
	width: ${(props) => (props.mobile ? '100%' : '300px')};
	display: flex;
	flex-direction: column;
	${(props) =>
		props.mobile &&
		css`
			height: 100%;
			margin-bottom: 0;
			border-radius: 0;
			border: none;
			border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
		`};

	${(props) =>
		!props.$display &&
		css`
			display: none;
		`}
`

const TableAlignment = css`
	justify-content: space-between;
	& > div:first-child {
		flex: 70 70 0 !important;
	}
	& > div:nth-child(2) {
		flex: 100 100 0 !important;
		justify-content: center;
	}
	& > div:last-child {
		flex: 60 60 0 !important;
		justify-content: flex-end;
		padding-right: 20px;
	}
`

const StyledTable = styled(Table)`
	border: none;
	overflow-y: auto;
	flex: 1;
	min-height: 0;

	.table-row,
	.table-body-row {
		${TableAlignment}
		padding: 0;
	}
	.table-body-cell {
		height: 30px;
	}
` as typeof Table

const PriceValue = styled(Body).attrs({ mono: true })`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	padding-left: 5px;
`

const TimeValue = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
`

const BookPriceValue = styled(PriceValue)<{ negative?: boolean; normal?: boolean }>`
	white-space: nowrap;
	color: ${(props) =>
		props.normal
			? props.theme.colors.selectedTheme.button.text.primary
			: props.theme.colors.selectedTheme[props.negative ? 'red' : 'green']};

	&:hover {
		text-decoration: underline;
	}
`

const BookVolumeValue = styled(PriceValue)<{ negative?: boolean; normal?: boolean }>`
	white-space: nowrap;
	color: ${(props) =>
		props.normal
			? props.theme.colors.selectedTheme.button.text.primary
			: props.theme.colors.selectedTheme[props.negative ? 'red' : 'green']};
`

const LiquidityAmount = styled.span`
	color: #3399ff;
	margin-left: 4px;
`

const TableContainer = styled.div`
	overflow: hidden;
	min-height: 0;
	flex: 1;
	display: flex;
	flex-direction: column;
`

const OrderbookHeader = styled(FlexDiv)`
	${TableAlignment}
	padding: 8px 0;
	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

const HeaderColumn = styled.div`
	&:first-child {
		padding-left: 18px;
	}
	&:last-child {
		padding-right: 18px;
		text-align: right;
	}
`

const SellTable = styled(StyledTable)`
	/* 卖单表格 - 红色 */
	.table-row {
		display: none;
	}
`

const BuyTable = styled(StyledTable)`
	/* 买单表格 - 绿色 */
	.table-row {
		display: none;
	}
`

const CurrentPriceSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 12px 0;
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	gap: 4px;
`

const CurrentPriceLabel = styled(Body)`
	font-size: 12px;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
`

const CurrentPriceValue = styled(Body).attrs({ mono: true })`
	font-size: 16px;
	font-weight: 600;
	color: ${(props) => props.theme.colors.selectedTheme.white};
`
